import axios from 'axios';
import { Toast } from 'vant';
import i18n from '../i18n';
import router from '../router';
import { getPayloadCode, getPayloadMessage, isSuccessPayload } from './response';
import { getErrorKey, getErrorParams } from './errorMessages';
import { clearToken, getToken } from '../utils/session';

const http = axios.create({
  baseURL: '',
  timeout: 20000
});

function errorMessage(code, fallbackKey = 'errors.requestFailed') {
  if (code && i18n.te(`errors.${code}`)) {
    return i18n.t(`errors.${code}`);
  }
  return fallbackKey ? i18n.t(fallbackKey) : '';
}

const passwordResetRoutes = new Set(['/crowd/auth/sendPasswordResetCode', '/crowd/auth/resetPassword']);
const isPasswordResetRoute = route => passwordResetRoutes.has(String(route || '').split('?')[0]);

const walletMaintenanceRoutes = new Set([
  '/crowd/wallet/pin',
  '/crowd/wallet/otp/relogin/send',
  '/crowd/wallet/otp/relogin/verify'
]);

function cleanWalletMaintenanceMessage(payload, route = '') {
  if (!walletMaintenanceRoutes.has(String(route || '').split('?')[0])) return '';
  // Known lock reasons must use the selected language, including on reconnect routes.
  if (['errors.walletPinLocked', 'errors.walletDeviceLocked'].includes(getErrorKey(payload, route))) return '';
  const message = String(getPayloadMessage(payload) || '').trim();
  if (!message || message.length > 140 || /[{}]|\bhttp\b|\/api\/|otp\/|reg\/login/i.test(message)) return '';
  return message;
}

http.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers['X-Crowd-Token'] = token;
  }
  return config;
});

http.interceptors.response.use(
  (response) => {
    const payload = response.data;
    if (!isSuccessPayload(payload)) {
      const code = getPayloadCode(payload);
      if ([401, 1288, 1289].includes(Number(code)) && !isPasswordResetRoute(response.config && response.config.url)) {
        clearToken();
        router.replace({ name: 'login' });
      }
      const route = response.config && response.config.url;
      const message = cleanWalletMaintenanceMessage(payload, route)
        || i18n.t(getErrorKey(payload, route), getErrorParams(payload));
      Toast(message);
      return Promise.reject(payload);
    }
    return payload.data;
  },
  (error) => {
    const status = error.response && error.response.status;
    if (status === 401 && !isPasswordResetRoute(error.config && error.config.url)) {
      clearToken();
      router.replace({ name: 'login' });
      Toast(errorMessage(401));
    } else if (error.code === 'ECONNABORTED') {
      Toast(i18n.t('errors.timeout'));
    } else if (error.message === 'Network Error') {
      Toast(i18n.t('errors.network'));
    } else {
      const payload = error.response && error.response.data;
      const route = error.config && error.config.url;
      Toast(cleanWalletMaintenanceMessage(payload, route) || i18n.t(payload && typeof payload === 'object' ? getErrorKey(payload, route)
        : route === '/crowd/wallet/cancelOffline' ? 'errors.cancelOfflineFailed'
          : ['/crowd/wallet/offline', '/crowd/wallet/resume'].includes((route || '').split('?')[0]) ? 'errors.businessStateFailed'
          : ['/crowd/wallet/pauseReceive', '/crowd/wallet/resumeReceive'].includes(route) ? 'errors.receiveChangeFailed' : 'errors.busy', getErrorParams(payload)));
    }
    return Promise.reject(error);
  }
);

export default http;
